import { FC, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import SplitIOTreatment from '../../../components/splitio/splitioTreatment';
import Input from '../Input';
import Button from '../Button';
import styled from 'styled-components';

const Title = styled.h1(({ theme }) => {
  return `
    font-size: ${theme.type.size.large};
    line-height: 1.75rem;
    text-align: center;
    font-weight: ${theme.type.weight.bold};
    margin-top: 2.5rem;
    margin-bottom: 2.5rem;
`;
});

const InputContainer = styled.div`
  margin: 2rem 0 0.75rem;
`;

const ButtonContainer = styled.div`
  margin-top: 5rem;

  &.error {
    margin-top: 2.75rem;
  }
`;

const ErrorMessage = styled.div(({ theme }) => {
  return `
    color: ${theme.color.primary.regular};
    font-style: italic;
    text-align: center;
    margin-top: 0.75rem;
  `;
});

const ForgotPasswordSection = styled.div`
  text-align: center;
  margin-top: 1rem;
`;

const ForgotPasswordLink = styled(Link)(({ theme }) => {
  const { type } = theme;

  return `
    font-size: ${type.size.small};
  `;
});

export interface ISignInForm {
  errorMessage: string | undefined;
  submitCallback: (username: string, password: string) => void;
  loading: boolean;
  disabled: boolean;
}

const SignInForm: FC<ISignInForm> = ({ errorMessage, submitCallback, loading, disabled }) => {
  const [username, setUsername] = useState<string>('');
  const [usernameError, setUsernameError] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<boolean>(false);

  const validateInputs = (): boolean => {
    setUsernameError(!username ? true : false);
    setPasswordError(!password ? true : false);

    if (!username || !password) {
      return false;
    }

    return true;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (!validateInputs()) {
      return;
    }

    submitCallback(username, password);
  };

  return (
    <div>
      <Title>Sign in to your account</Title>

      <form onSubmit={handleSubmit}>
        <InputContainer>
          <Input
            className={usernameError ? 'error' : ''}
            placeholder="Enter your username or email"
            onChange={(e) => setUsername(e.currentTarget.value)}
            value={username}
            id="loginUsername"
            name="username"
            label="Email / Username"
            errors="Username can not be empty"
            invalid={usernameError}
            disabled={disabled}
            required
          />
        </InputContainer>

        <InputContainer>
          <Input
            className={passwordError ? 'error' : ''}
            type="password"
            placeholder="Enter your password"
            onChange={(e) => setPassword(e.currentTarget.value)}
            value={password}
            id="loginPassword"
            name="password"
            label="Password"
            errors="Password cannot be empty"
            invalid={passwordError}
            disabled={disabled}
            required
          />
        </InputContainer>

        {loading && (
          <div className="ad-table-loader">
            <span className="loading-title">Loading</span>

            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        )}

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <ButtonContainer className={errorMessage ? 'error' : ''}>
          <Button type="submit" disabled={disabled} fullWidth>
            SIGN IN
          </Button>
        </ButtonContainer>

        <SplitIOTreatment featureName={'Forgot-Password'} treatmentValue="on">
          {(treatment: string) => {
            if (treatment === 'on' && !disabled) {
              return (
                <ForgotPasswordSection>
                  <ForgotPasswordLink to="/forgot-password">Trouble logging in?</ForgotPasswordLink>
                </ForgotPasswordSection>
              );
            } else {
              return null;
            }
          }}
        </SplitIOTreatment>
      </form>
    </div>
  );
};

export default SignInForm;
